import React from 'react';
import { Home, Droplet, ShowerHead, Layers, Shield, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';

export const Residential = () => {
  const commonProblems = [
    {
      problem: 'Ceiling leakage from terrace',
      impact: 'Water stains, paint damage, electrical hazards',
      solution: 'Professional terrace waterproofing with warranty'
    },
    {
      problem: 'Damp walls and moisture',
      impact: 'Mold growth, health issues, property devaluation',
      solution: 'External and internal wall treatment'
    },
    {
      problem: 'Bathroom seepage to lower floors',
      impact: 'Neighbor disputes, expensive repairs',
      solution: 'Complete bathroom waterproofing system'
    },
    {
      problem: 'Basement water accumulation',
      impact: 'Unusable space, foundation weakening',
      solution: 'Crystalline waterproofing and drainage'
    }
  ];

  const benefits = [
    'Long-lasting solutions, not temporary fixes',
    'Minimal disruption to daily life',
    'Clean and professional execution',
    'Transparent pricing with no hidden costs',
    'Warranty-backed workmanship',
    'Experienced team with 26 years of expertise'
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-slate-50 to-white py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center px-4 py-2 bg-slate-100 rounded-full text-sm font-medium text-slate-700 mb-6">
              <Home className="w-4 h-4 mr-2" />
              2000+ Homes Protected
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              Residential Waterproofing Solutions
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Stop water damage before it becomes costly. Professional waterproofing for homes with lasting results.
            </p>
          </div>
        </div>
      </section>

      {/* Common Problems Section */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Homeowner Waterproofing Challenges
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Common problems we solve for homeowners every day.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {commonProblems.map((item, index) => (
              <div key={index} className="bg-slate-50 rounded-lg p-6 border border-slate-200">
                <h3 className="text-lg font-semibold text-slate-900 mb-3">{item.problem}</h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-medium text-slate-700 mb-1">Impact:</p>
                    <p className="text-sm text-slate-600">{item.impact}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-700 mb-1">Our Solution:</p>
                    <div className="flex items-start space-x-2">
                      <CheckCircle2 className="w-4 h-4 text-slate-700 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-slate-600">{item.solution}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Homeowners Choose Us */}
      <section className="py-16 lg:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6">
                Why 5000+ Homeowners Trust Us
              </h2>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-slate-700 mt-0.5 flex-shrink-0" />
                    <span className="text-base text-slate-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-lg overflow-hidden shadow-lg">
              <img 
                src="https://customer-assets.emergentagent.com/job_leakdefense-pros/artifacts/ak8w5cr7_ChatGPT%20Image%20Mar%2029%2C%202026%20at%2002_54_45%20PM.png"
                alt="Happy Homeowner"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Our Simple Process
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '1', title: 'Site Inspection', desc: 'Free inspection to assess the problem' },
              { step: '2', title: 'Solution Design', desc: 'Customized plan with transparent pricing' },
              { step: '3', title: 'Professional Execution', desc: 'Skilled team with quality materials' },
              { step: '4', title: 'Quality Check & Warranty', desc: 'Thorough inspection and warranty coverage' }
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-16 h-16 bg-slate-800 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-slate-800 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
            Protect Your Home Today
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            Get a free site inspection and honest assessment of your waterproofing needs.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-white text-slate-800 rounded-lg text-base font-semibold hover:bg-slate-100 transition-colors"
          >
            Schedule Free Inspection
          </Link>
        </div>
      </section>
    </div>
  );
};